import { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Container, Flex } from 'src/components/ui';
import { AuthHeader } from 'src/components/auth';
import FusionLoading from 'src/components/common/FusionLoading';
import AlertMessage from 'src/components/common/AlertMessage';
import UserContext from 'src/contexts/user/UserContext';
import authService from 'src/services/auth';
import { toastSuccess } from 'src/services/toast';

// VITE_API_URL includes /v1, so we use it directly
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/v1';

const ShopifyCallback = () => {
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [searchParams] = useSearchParams();
	const { setUser } = useContext(UserContext);

	useEffect(() => {
		const token = searchParams.get('token');
		const productCount = searchParams.get('productCount');
		const catalogId = searchParams.get('catalogId');
		const locationIds = searchParams.get('locationIds');
		const serviceAreaIds = searchParams.get('serviceAreaIds');
		const fromShopify = searchParams.get('fromShopify');
		const prefillCatalogId = searchParams.get('prefillCatalogId');
		const aiPrefill = searchParams.get('aiPrefill');
		const redirectTo = searchParams.get('redirectTo');

		if (!token) {
			setError('Invalid callback. Token is missing.');
			setIsLoading(false);
			return;
		}
		handleShopifyCallback(token, productCount, catalogId, locationIds, serviceAreaIds, fromShopify, prefillCatalogId, aiPrefill, redirectTo);
	}, []);

	const handleShopifyCallback = async (
		token: string,
		productCount: string | null,
		catalogId: string | null,
		locationIds: string | null,
		serviceAreaIds: string | null,
		fromShopifyParam: string | null,
		prefillCatalogIdParam: string | null,
		aiPrefillParam: string | null,
		redirectToParam: string | null
	) => {
		try {
			console.log('🔑 ShopifyCallback: Received token:', token?.substring(0, 50) + '...');

			// Fetch the current user directly with the token (bypass interceptor)
			const response = await axios.get(`${API_URL}/users/me`, {
				headers: {
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json',
				},
			});

			const user = response.data;
			console.log('🔑 ShopifyCallback: User fetched:', user?.email);

			if (user) {
				// Store the token in localStorage for future requests
				const tokens = {
					access: {
						token,
						expires: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
					},
					refresh: {
						token: '',
						expires: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
					},
				};
				authService.setTokens(tokens);
				authService.setCurrentUser(user);
				setUser(user);
				toastSuccess('Successfully connected from Shopify!');

				// If redirectTo is specified and no prefill params, use it directly
				if (redirectToParam && !prefillCatalogIdParam && aiPrefillParam !== 'true') {
					navigate(redirectToParam);
					return;
				}

				// Build redirect URL with all prefill params
				let redirectUrl = '/projects/campaigns/new?fromShopify=true';

				// Use prefillCatalogId from params if available, otherwise fall back to catalogId for single product
				const effectiveCatalogId = prefillCatalogIdParam || (productCount === '1' ? catalogId : null);
				if (effectiveCatalogId) {
					redirectUrl += `&prefillCatalogId=${effectiveCatalogId}`;
				}
				if (locationIds) {
					redirectUrl += `&prefillLocationIds=${locationIds}`;
				}
				if (serviceAreaIds) {
					redirectUrl += `&prefillServiceAreaIds=${serviceAreaIds}`;
				}
				// Pass aiPrefill param if present
				if (aiPrefillParam === 'true') {
					redirectUrl += '&aiPrefill=true';
				}
				navigate(redirectUrl);
			} else {
				setError('Failed to load user data. Please try again.');
			}
		} catch (error: any) {
			console.error('Shopify callback error:', error);
			const message = error.response?.data?.message || 'Failed to authenticate. Please try again.';
			setError(message);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Container maxW="md">
			<Flex direction="column" gap={12}>
				<AuthHeader title="Connecting from Shopify..." />
				{error && <AlertMessage status="error">{error}</AlertMessage>}
				<FusionLoading isLoading={isLoading} />
			</Flex>
		</Container>
	);
};

export default ShopifyCallback;
